import { OktaAuth, toRelativeUrl, UserClaims } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";
import {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useHistory } from "react-router-dom";

import { getOktaAuth } from "../config/okta";

interface LoggedUser {
  id: string | undefined;
  email: UserClaims["email"];
  name: UserClaims["name"];
  givenName: UserClaims["given_name"];
}
interface AuthContextType {
  oktaAuth?: OktaAuth;
  login: () => Promise<void>;
  user?: LoggedUser | undefined;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const oktaAuth = getOktaAuth();
  const history = useHistory();
  const [user, setUser] = useState<LoggedUser | undefined>(undefined);

  const login = useCallback(async () => {
    if (oktaAuth) {
      await oktaAuth.signInWithRedirect();
      return;
    } else {
      history.replace("/");
    }
  }, [oktaAuth, history]);

  const restoreOriginalUri = useCallback(
    async (_oktaAuth: OktaAuth, originalUri: string) => {
      history.replace(
        toRelativeUrl(originalUri || "/", window.location.origin)
      );
    },
    [history]
  );

  const handleAuthRequired = useCallback(async () => {
    history.push("/login");
  }, [history]);

  useEffect(() => {
    (async () => {
      const isAuthenticated = await oktaAuth?.isAuthenticated();
      if (isAuthenticated) {
        const id = oktaAuth?.getIdToken();
        const user = await oktaAuth?.getUser();
        setUser({
          id,
          email: user?.email,
          name: user?.name,
          givenName: user?.given_name,
        });
      } else {
        setUser(undefined);
      }
    })();
  }, [oktaAuth]);

  if (!oktaAuth) {
    return (
      <AuthContext.Provider value={{ login }}>{children}</AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={{ oktaAuth, login, user }}>
      <Security
        oktaAuth={oktaAuth}
        onAuthRequired={handleAuthRequired}
        restoreOriginalUri={restoreOriginalUri}
      >
        {children}
      </Security>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
